import {
    useCallback, useRef,
} from 'react'
import isEqual from 'lodash/isEqual'
import {
    useBlocker, useNavigate,
} from 'react-router-dom'

import UnsavedChangesPrompt from '../UnsavedChangesPrompt'

type Props = {
    when: boolean,
    discard?: () => void,
    action?: (...a: any[]) => Promise<any> | any,
    promptText?: string,
    positiveLabel?: string,
    isPositiveAlert?: boolean,
    dialogTitle?: string,
}

const HistoryBlockPrompt = ({
    when,
    action,
    promptText = 'Would you like to save your changes before you leave this page?',
    discard,
    positiveLabel,
    isPositiveAlert = false,
    dialogTitle,
}: Readonly<Props>) => {
    const navigate = useNavigate()
    const skipBlockRef = useRef(false)
    const pendingLocationRef = useRef<string | null>(null)
    // eslint-disable-next-line no-spaced-func, func-call-spacing
    const blockerRef = useRef<{state: 'unblocked' | 'blocked' | 'proceeding', reset: () => void}>({
        state: 'unblocked',
        reset() {},
    })

    const {
        state, reset,
    } = useBlocker(({
        currentLocation, nextLocation,
    }) => {
        if (skipBlockRef.current) return false

        if (when && !isEqual(currentLocation, nextLocation)) {
            pendingLocationRef.current = nextLocation?.pathname
        }

        return when
    })

    blockerRef.current = {
        state,
        reset,
    }

    // Bypasses the blocker for the post-action/discard navigation.
    // We can't use `proceed()` because the blocker may have auto-unblocked
    // mid-save (e.g. `pending` flips `when` to false), and we can't use
    // a plain `navigate()` because `when` may be true again after save.
    // `skipBlockRef` makes the blocker return false for exactly one
    // `navigate()` call, so the redirect always goes through.
    const navigateToDestination = useCallback(() => {
        const destination = pendingLocationRef.current

        if (!destination) return

        if (blockerRef.current.state === 'blocked') {
            blockerRef.current.reset()
        }

        skipBlockRef.current = true

        try {
            navigate(destination)
        } finally {
            skipBlockRef.current = false
        }
    }, [navigate])

    const handleAction = useCallback(async () => {
        try {
            if (action) {
                const result = action()

                if (result instanceof Promise) {
                    await result
                }
            }

            navigateToDestination()
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error)
        }
    }, [
        action,
        navigateToDestination,
    ])

    const handleDiscard = useCallback(() => {
        discard()
        navigateToDestination()
    }, [
        discard,
        navigateToDestination,
    ])

    return (
        <UnsavedChangesPrompt
            action={handleAction}
            discard={discard ? handleDiscard : null}
            open={state === 'blocked'}
            close={reset}
            promptText={promptText}
            positiveLabel={positiveLabel}
            isPositiveAlert={isPositiveAlert}
            dialogTitle={dialogTitle}
        />
    )
}

export default HistoryBlockPrompt
